import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API}/api`,
});

// Add token to all requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authApi = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
  validateReferral: (code) => api.get(`/auth/validate-referral/${code}`),
  sendVerification: (email, name) => api.post('/auth/send-verification', { email, name }),
  verifyCode: (email, code, purpose) => api.post('/auth/verify-code', { email, code, purpose }),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
};

// User Dashboard APIs
export const userApi = {
  getDashboard: () => api.get('/user/dashboard'),
  getReferral: () => api.get('/user/referral'),
  getTransactions: (type) => api.get('/user/transactions', { params: { type } }),
  getStarProgress: () => api.get('/user/star-progress'),
  getPromotionProgress: () => api.get('/user/promotion-progress'),
  getTierProgress: () => api.get('/user/tier-progress'),
  transferRoiToCash: () => api.post('/user/transfer-roi-to-cash'),
  getActiveBanners: () => api.get('/user/active-banners'),
  dismissBanner: (promoId) => api.post(`/user/dismiss-banner/${promoId}`),
  getPromotionWallet: () => api.get('/user/promotion-wallet'),
  transferPromoToCash: () => api.post('/user/transfer-promo-to-cash'),
  getSalaryWallet: () => api.get('/user/salary-wallet'),
  transferSalaryToCash: () => api.post('/user/transfer-salary-to-cash'),
  getRoiInfo: () => api.get('/user/roi-info'),
  getWithdrawalInfo: () => api.get('/user/withdrawal-info'),
  getAnalytics: () => api.get('/user/analytics'),
  exportTransactions: (format, startDate, endDate) => api.get('/user/transactions/export', { 
    params: { format, start_date: startDate, end_date: endDate },
    responseType: format === 'csv' ? 'blob' : 'json'
  }),
  deactivateAccount: () => api.post('/user/deactivate'),
  reinvestCapital: (investmentId) => api.post(`/investments/${investmentId}/reinvest`),
};

// Deposit APIs
export const depositApi = {
  create: (data) => api.post('/deposits', data),
  getAll: () => api.get('/deposits'),
};

// Withdrawal APIs
export const withdrawalApi = {
  create: (data) => api.post('/withdrawals', data),
  getAll: () => api.get('/withdrawals'),
};

// Package APIs
export const packageApi = {
  getActive: () => api.get('/packages'),
  getAll: () => api.get('/packages/all'),
  create: (data) => api.post('/packages', data),
  update: (id, data) => api.put(`/packages/${id}`, data),
  toggle: (id) => api.patch(`/packages/${id}/toggle`),
};

// Investment APIs
export const investmentApi = {
  create: (data) => api.post('/investments', data),
  getAll: () => api.get('/investments'),
};

// Transfer APIs
export const transferApi = {
  create: (data) => api.post('/transfers', data),
  getAll: () => api.get('/transfers'),
};

// Star APIs
export const starApi = {
  getAll: () => api.get('/stars'),
  create: (data) => api.post('/stars', data),
  update: (id, data) => api.put(`/stars/${id}`, data),
  toggle: (id) => api.patch(`/stars/${id}/toggle`),
};

// Promotion APIs
export const promotionApi = {
  getActive: () => api.get('/promotions'),
  getAll: () => api.get('/promotions/all'),
  create: (data) => api.post('/promotions', data),
  update: (id, data) => api.put(`/promotions/${id}`, data),
  toggle: (id) => api.patch(`/promotions/${id}/toggle`),
};

// Settings APIs
export const settingsApi = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
};

// Admin APIs
export const adminApi = {
  getStats: () => api.get('/admin/stats'),
  getUsers: (params) => api.get('/admin/users', { params }),
  getUser: (id) => api.get(`/admin/users/${id}`),
  toggleUserStatus: (id) => api.patch(`/admin/users/${id}/toggle-status`),
  verifyUser: (id) => api.patch(`/admin/users/${id}/verify`),
  adjustBalance: (data) => api.post('/admin/users/adjust-balance', data),
  loginAsUser: (id) => api.post(`/admin/login-as-user/${id}`),
  getDeposits: (status) => api.get('/admin/deposits', { params: { status } }),
  processDeposit: (id, data) => api.patch(`/admin/deposits/${id}`, data),
  getWithdrawals: (status) => api.get('/admin/withdrawals', { params: { status } }),
  processWithdrawal: (id, data) => api.patch(`/admin/withdrawals/${id}`, data),
  distributeROI: () => api.post('/admin/distribute-roi'),
  distributeStarSalaries: () => api.post('/admin/distribute-star-salaries'),
  processMonthlySalaries: () => api.post('/admin/process-monthly-salaries'),
  getTickets: () => api.get('/admin/support/tickets'),
  replyTicket: (id, data) => api.post(`/admin/support/tickets/${id}/reply`, data),
  updateTicketStatus: (id, status) => api.put(`/admin/support/tickets/${id}/status?status=${status}`),
};

export default api;
